/**
 * ============================================
 * COMMON SECTION - SSL & DOMAINS
 * ============================================
 * 
 * Shows:
 *   - SSL Certificates (valid/expiring/expired counts + list)
 *   - Registered Domains (count + list)
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Shield, Globe, CheckCircle2, XCircle, Clock, ExternalLink } from 'lucide-react';
import { useCertificates } from '@/hooks';

export function CommonSection() {
  const { certificates, domainsPurchased, isLoading, error } = useCertificates();

  // Count certificates by status
  const validCerts = certificates.filter(c => 
    c.status === 'VALID' || c.status === 'ISSUED'
  );
  const expiringSoonCerts = certificates.filter(c => 
    c.status === 'EXPIRING_SOON' || c.status === 'CRITICAL'
  );
  const expiredOrInvalidCerts = certificates.filter(c => 
    c.status === 'EXPIRED' || c.status === 'VALIDATION_TIMED_OUT' || c.status === 'PENDING_VALIDATION'
  );

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-muted-foreground flex items-center gap-2">
        <Shield className="h-5 w-5" />
        SSL & Domains
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SSL Certificates */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-500" />
              SSL Certificates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-20 bg-muted animate-pulse rounded" />
            ) : error ? (
              <div className="text-sm text-red-400">{error}</div>
            ) : (
              <div className="space-y-3">
                {/* Status Counts */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">Valid</span>
                  </div>
                  <span className="font-bold text-green-500">{validCerts.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-muted-foreground">Expiring Soon</span>
                  </div>
                  <span className="font-bold text-amber-500">{expiringSoonCerts.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">Expired/Invalid</span>
                  </div>
                  <span className="font-bold text-red-500">{expiredOrInvalidCerts.length}</span>
                </div>
                
                {/* Certificate List */}
                {certificates.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">All SSL Certificates:</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {certificates.map((cert, idx) => {
                        const isValid = cert.status === 'VALID' || cert.status === 'ISSUED';
                        const isExpiring = cert.status === 'EXPIRING_SOON' || cert.status === 'CRITICAL';
                        const isExpired = cert.status === 'EXPIRED' || cert.status === 'VALIDATION_TIMED_OUT';
                        
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/20">
                            <div className="flex items-center gap-2">
                              {isValid && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                              {isExpiring && <Clock className="h-3 w-3 text-amber-500" />}
                              {isExpired && <XCircle className="h-3 w-3 text-red-500" />}
                              {!isValid && !isExpiring && !isExpired && <Shield className="h-3 w-3 text-gray-500" />}
                              <a 
                                href={`https://${cert.domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline flex items-center gap-1"
                              >
                                {cert.domain}
                                <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                              </a>
                            </div>
                            <span className={
                              isValid ? 'text-green-500' :
                              isExpiring ? 'text-amber-500' :
                              isExpired ? 'text-red-500' :
                              'text-gray-500'
                            }>
                              {cert.days_remaining !== null && cert.days_remaining !== undefined 
                                ? `${cert.days_remaining}d` 
                                : cert.status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registered Domains */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Globe className="h-4 w-4 text-purple-500" />
              Registered Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-20 bg-muted animate-pulse rounded" />
            ) : (
              <div>
                <p className="text-3xl font-bold mb-2">{domainsPurchased.length}</p>
                <p className="text-xs text-muted-foreground mb-3">Registered domains</p>
                
                {/* Domain List */}
                {domainsPurchased.length > 0 && (
                  <div className="pt-3 border-t border-border">
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {domainsPurchased.map((domain, idx) => {
                        // Calculate days until expiry
                        let daysUntilExpiry = null;
                        if (domain.expiry) {
                          const expiry = new Date(domain.expiry);
                          const now = new Date();
                          daysUntilExpiry = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                        }
                        
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs p-2 rounded bg-muted/20">
                            <div className="flex items-center gap-2">
                              <Globe className="h-3 w-3 text-purple-500" />
                              <span className="font-medium">{domain.domain_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              {domain.auto_renew && (
                                <span className="text-green-500 text-[10px]">AUTO-RENEW</span>
                              )}
                              {daysUntilExpiry !== null && (
                                <span className={daysUntilExpiry < 30 ? 'text-amber-500' : ''}>
                                  {daysUntilExpiry}d
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
